import React from "react";
import { Link } from "react-router-dom";
import {
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ClipboardIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import MarketingLayout from "../components/MarketingLayout";
import SEO from "../components/SEO";

const featureCards = [
  {
    icon: UserGroupIcon,
    title: "Player Management",
    description: "Import rosters from TeamSnap, SportsEngine, and more.",
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: "Evaluation Sessions",
    description:
      "Run structured evaluations with customizable skills. Multiple evaluators can score simultaneously.",
  },
  {
    icon: ChartBarIcon,
    title: "Skills-Based Rating",
    description:
      "Weighted scoring with per-player breakdowns, rankings, and percentile comparisons.",
  },
  {
    icon: EnvelopeIcon,
    title: "Parent Reports",
    description:
      "Email secure report links to parents with scores, rankings, and evaluator feedback.",
  },
  {
    icon: ShieldCheckIcon,
    title: "Role-Based Access",
    description:
      "Admins, coordinators, coaches, and evaluators each see exactly what they need.",
  },
  {
    icon: DevicePhoneMobileIcon,
    title: "Works Everywhere",
    description:
      "Fully responsive. Score players on the field from any device.",
  },
];

// const stats = [
//   { value: "10,000+", label: "Players Evaluated" },
//   { value: "100,000+", label: "Scores Collected" },
//   { value: "500+", label: "Hours Saved" },
// ];

export default function LandingPage() {
  return (
    <MarketingLayout>
      <SEO
        path="/"
        description="Eval Ninja simplifies player evaluations for youth sports clubs. Run tryouts, assign teams, and share parent reports — all from one platform."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Eval Ninja",
            applicationCategory: "SportApplication",
            operatingSystem: "Web",
            url: "https://evalninja.app",
            description:
              "Player evaluation platform for youth sports clubs. Run tryouts, assign teams, and share parent reports.",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
      </SEO>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-sky-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight">
                Player Try-outs & Evaluations
                <span className="text-sky-600"> Made Easy</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-lg">
                The all-in-one platform for running player evaluations, tracking
                skills, building balanced teams, and sharing results with
                parents. Used by clubs across every sport.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 transition-colors shadow-sm"
                >
                  Contact Us To Get Started
                </Link>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  See How It Works
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-sky-100 to-slate-100 rounded-2xl blur-2xl opacity-60" />
                <div className="relative">
                  <div
                    className="relative flex items-center justify-center"
                    style={{ gap: "10px" }}
                  >
                    <img
                      src="/assets/score_card.png"
                      alt="Score Card Preview"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {/* {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl font-bold text-white">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-300 mt-1">{stat.label}</div>
              </div>
            ))} */}
          </div>
        </div>
      </section>

      {/* Feature section 1: Image left, Text right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                <ClipboardDocumentCheckIcon className="w-4 h-4" />
                Evaluation Type
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                Choose the type of evaluation session you want to run
              </h2>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                Works for both skill based assessments and group based
                evaluations. Set up your session in minutes and start running
                evaluations the way you want.
              </p>
            </div>
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-sky-50 to-slate-50 rounded-2xl blur-xl opacity-60" />
              <div className="relative">
                <div
                  className="relative flex items-center justify-center"
                  style={{ gap: "10px" }}
                >
                  <img
                    src="/assets/evaluation_type.png"
                    alt="Evaluation Type Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature section 1: Text left, Image right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                <ClipboardDocumentCheckIcon className="w-4 h-4" />
                Evaluations
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                Run Structured Evaluations with Ease
              </h2>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                Create evaluation sessions with custom skill categories and
                rating scales. Multiple evaluators can score players
                simultaneously from any device, with scores syncing in
                real-time.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Custom skills and weighted scoring",
                  "Multiple simultaneous evaluators",
                  "Works on phones and tablets",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-gray-600"
                  >
                    <svg
                      className="w-5 h-5 text-sky-500 mt-0.5 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-sky-50 to-slate-50 rounded-2xl blur-xl opacity-60" />
              <div className="relative">
                <div
                  className="relative flex items-center justify-center"
                  style={{ gap: "10px" }}
                >
                  <img
                    src="/assets/phone_1.png"
                    alt="Evaluation Session Preview"
                    style={{ maxHeight: "375px", width: "auto" }}
                  />
                  <img
                    src="/assets/phone_2.png"
                    alt="Evaluation Session Preview"
                    style={{ maxHeight: "375px", width: "auto" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature section 2: Image left, Text right */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                <ChartBarIcon className="w-4 h-4" />
                Rankings
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                Data-Driven Player Rankings
              </h2>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                See instant rankings with per-player skill breakdowns,
                percentile comparisons, and weighted averages. Make fair,
                objective team placement decisions backed by real data.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Weighted skill scoring and averages",
                  "Percentile rankings across your roster",
                  "Easily build teams with our innovative team builder tool",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-gray-600"
                  >
                    <svg
                      className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-slate-50 to-sky-50 rounded-2xl blur-xl opacity-60" />
              <div className="relative">
                <div
                  className="relative flex items-center justify-center"
                  style={{ gap: "10px" }}
                >
                  <img
                    src="/assets/score_card_screen.png"
                    alt="Score Card Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature section 4: Text left, Image right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                <EnvelopeIcon className="w-4 h-4" />
                Parent Reports
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                Keep Parents in the Loop
              </h2>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                Automatically email secure, personalized report links to
                parents. Each report shows their child's scores, rankings among
                peers, and evaluator feedback — all without revealing other
                players' data.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "One-click bulk email to all parents",
                  "Secure, token-based report links",
                  "Individual scores with peer percentiles",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-gray-600"
                  >
                    <svg
                      className="w-5 h-5 text-amber-500 mt-0.5 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-50 to-sky-50 rounded-2xl blur-xl opacity-60" />
              <div className="relative">
                <div
                  className="relative flex items-center justify-center"
                  style={{ gap: "10px" }}
                >
                  <img
                    src="/assets/parent_report.png"
                    alt="Parent Report Preview"
                    style={{ maxHeight: "400px", width: "auto" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything You Need
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              From roster import to parent report delivery, Eval Ninja handles
              the entire evaluation workflow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featureCards.map((feature) => (
              <div
                key={feature.title}
                className="bg-white rounded-xl p-6 hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Built for Every Role
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you're running the show or scoring on the field, Eval
              Ninja adapts to your needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                icon: StarIcon,
                title: "Club Directors",
                description:
                  "Oversee all evaluations, manage staff, customize skill categories, and deliver professional parent reports that elevate your club's reputation.",
              },
              {
                icon: ClipboardIcon,
                title: "Coaches",
                description:
                  "Manage evaluation sessions, view detailed player skill breakdowns and rankings, and use data-driven insights to build balanced teams.",
              },
              {
                icon: ArrowTrendingUpIcon,
                title: "Evaluators",
                description:
                  "Score players quickly from any device with an intuitive interface. Add notes and feedback that gets anonymously delivered directly to parents along with scores and rankings.",
              },
              {
                icon: UsersIcon,
                title: "Parents",
                description:
                  "Receive a secure, personalized report showing your child's scores, percentile rankings, and evaluator feedback.",
              },
            ].map((role) => (
              <div
                key={role.title}
                className="text-center p-8 rounded-xl bg-gray-50 border border-gray-100"
              >
                <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <role.icon className="w-7 h-7 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {role.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {role.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-slate-700 to-slate-800 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Elevate Your Evaluations?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Join clubs that have streamlined their player evaluation process
            with Eval Ninja.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-md text-slate-800 bg-white hover:bg-gray-100 transition-colors shadow-sm"
            >
              Contact Us To Get Started
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white/30 text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
